/**
 * The projects domain's face (ADR 0014, Rule D).
 *
 * Every file outside `src/projects/` reaches this domain through this module; nothing below it is a
 * path another domain may name. Files inside use relative paths.
 *
 * **Explicit named exports, never `export *`.** A face is an interface, not a bag.
 *
 * This face has real inbound edges rather than existing for its own sake (ADR 0011:118-119):
 * `useProjects` is called from `navigation/sidebar-nav.tsx` (#316, step 8) and from
 * `capture/prompt-capture-modal.tsx`, and both route files take a page component.
 *
 * **No cycle.** This domain reads `@/feed`, `@/entries`, `@/capture` and `@/document`, and none of
 * them imports `@/projects` — so unlike `feed/` <-> `entries/`, nothing here depends on the
 * function-body discipline ADR 0014:108-112 states.
 *
 * @see CONTEXT.md for what a project means
 */

// ============================================================================
// The pages
//
// Both route files keep the export Next.js resolves by name and delegate here (ADR 0010).
// ============================================================================

export { ProjectsPage } from './projects-page'
export { ProjectPage } from './project-page'

// ============================================================================
// Reads
//
// `useProjectStats` is not published: its only callers are `projects-page.tsx` and
// `project-sidebar.tsx`, both inside. The pure selectors stay internal for the reason every domain
// keeps them internal — a caller outside holding a `DocumentView` and a selector would be reading
// the document without the subscription `useDocumentView()` carries.
// ============================================================================

export { useProjects } from './select-projects'
export type { ProjectStats, ProjectTaskCounts } from './select-project-stats'

// ============================================================================
// Surfaces
//
// `ProjectSidebar` is rendered by `entries/task/`'s detail surfaces; `ProjectDetail`,
// `ProjectList` and `ProjectCreateForm` are composed by the two pages above and stay internal.
// ============================================================================

export { ProjectSidebar } from './project-sidebar'

// ============================================================================
// Writes
//
// Callers of `document/operations/projects.ts`, not writers (ADR 0012 rule 2). Neither wraps the
// write in a `useMutation` and neither touches a query key — rule 6, and #315's criterion. Both
// are internal today: the create form and the sidebar are this domain's own.
// ============================================================================

export type { ProjectDraft, ProjectPatch } from './write-spec'
